import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { createMix, audioContext, type Mix } from "./audio";
import { paint, type Scene } from "./compositor";
import { CANVAS_FPS, CANVAS_HEIGHT, CANVAS_WIDTH } from "./layouts";
import { isBareCamera } from "./scene";
import { publish, type Publication } from "./whip";
import { SignalError } from "../../lib/webrtc";

/**
 * Everything the studio owns that React does not: devices, a render loop and a
 * peer connection.
 *
 * ## The canvas is a ref, not state
 *
 * It is painted thirty times a second. Anything that made React re-render at
 * that rate would spend the frame budget reconciling instead of drawing, and
 * the encoder would get a stuttering picture. So the scene arrives through a
 * ref that the loop reads, and React re-renders only when something a human
 * changed changes.
 *
 * ## Two canvases, one drawing
 *
 * The captured canvas is 720p regardless of the window; the preview is
 * whatever fits on screen. Both are painted from the same scene by the same
 * function on the same frame, which is what makes the preview trustworthy -
 * a preview drawn by different code is a preview that can be wrong exactly
 * when it matters.
 */

export type StudioStatus =
  "idle" | "starting" | "live" | "reconnecting" | "error";

/**
 * How many times a dropped broadcast tries to get itself back.
 *
 * Six, with the delay doubling to a cap of eight seconds, is about a minute of
 * trying. Long enough to ride out a router reboot or a laptop changing
 * networks; short enough that a host whose internet is genuinely gone finds out
 * rather than watching a spinner for the rest of their set.
 */
const RECONNECT_ATTEMPTS = 6;

export interface Devices {
  cameras: MediaDeviceInfo[];
  microphones: MediaDeviceInfo[];
}

/**
 * Open a device, falling back to the system default if that exact one is gone.
 *
 * ## Why this exists now
 *
 * Device choices are remembered between sessions, and a remembered device id is
 * the least durable thing in the studio's settings: browsers reissue ids when
 * site permissions are reset, and the webcam that produced one may simply not
 * be plugged in today. `deviceId: { exact }` against a device that is not there
 * throws `OverconstrainedError`, so without this a host who unplugged a webcam
 * would find the studio refusing to start a camera at all - with an error
 * naming a device they had forgotten they ever selected.
 *
 * The stored preference is deliberately *not* cleared. They may well plug it
 * back in tomorrow, and quietly forgetting their choice because the cable was
 * out is worse than falling back for one session. The picker shows the default
 * while the device is missing - see `StudioColumn` - so nothing claims to be
 * using a camera it is not.
 */
async function withDevice(
  deviceId: string | undefined,
  open: (id?: string) => Promise<MediaStream>,
): Promise<MediaStream> {
  try {
    return await open(deviceId);
  } catch (cause) {
    const missing =
      cause instanceof DOMException &&
      (cause.name === "OverconstrainedError" || cause.name === "NotFoundError");

    // A refusal is not a missing device: `NotAllowedError` means the host said
    // no, and retrying without the constraint would just ask them again.
    if (!deviceId || !missing) throw cause;

    return open(undefined);
  }
}

export function useStudio(scene: Scene, active: boolean) {
  /*
   * The scene, mirrored into a ref for the loop.
   *
   * The loop is started once and lives for the session; it cannot close over
   * `scene` or it would paint whatever the scene was on the render that started
   * it, forever. Writing on every render is cheap and keeps the loop reading
   * the current one.
   */
  const sceneRef = useRef(scene);
  sceneRef.current = scene;

  const outputRef = useRef<HTMLCanvasElement | null>(null);
  const previewRef = useRef<HTMLCanvasElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const cameraRef = useRef<MediaStream | null>(null);
  const micRef = useRef<MediaStream | null>(null);
  const mixRef = useRef<Mix | null>(null);
  const publicationRef = useRef<Publication | null>(null);

  /*
   * The stream being published, kept across reconnects.
   *
   * A reconnect must re-publish *this* stream rather than build a new one. The
   * canvas capture and the audio mix are the broadcast - rebuilding them would
   * re-tap the deck (impossible, once per element) and hand the encoder a new
   * set of tracks for no reason.
   */
  const outgoingRef = useRef<MediaStream | null>(null);
  /** The composited canvas, captured once and kept for the session. */
  const canvasTrackRef = useRef<MediaStreamTrack | null>(null);
  const whipRef = useRef<string | null>(null);
  const retryRef = useRef<number | null>(null);
  /** Cancels a scheduled reconnect. Set by `start`, called by `stop`. */
  const retryClearRef = useRef<(() => void) | null>(null);

  const [status, setStatus] = useState<StudioStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [connection, setConnection] = useState<RTCPeerConnectionState>("new");
  const [devices, setDevices] = useState<Devices>({
    cameras: [],
    microphones: [],
  });
  const [cameraOn, setCameraOn] = useState(false);
  /*
   * Bumped every time the camera is (re)started.
   *
   * `cameraOn` does not change when the host switches device, but the track
   * does - and in passthrough that track *is* the broadcast, so the swap below
   * needs something to notice.
   */
  const [cameraGeneration, setCameraGeneration] = useState(0);
  const [micOn, setMicOn] = useState(false);
  const [level, setLevel] = useState(0);

  /* ---------------------------------------------------------------------- */
  /* The render loop                                                         */
  /* ---------------------------------------------------------------------- */

  useEffect(() => {
    // Nothing to paint and nothing to paint onto. Every host has this hook
    // mounted whether or not they opened the studio, and a `requestAnimationFrame`
    // loop that wakes sixty times a second to find two null refs is a battery
    // cost paid by people who are not using the feature.
    if (!active) return;

    let frame = 0;

    const draw = () => {
      frame = requestAnimationFrame(draw);

      const current = sceneRef.current;
      const withCamera: Scene = { ...current, camera: videoRef.current };

      const output = outputRef.current;

      if (output) {
        const ctx = output.getContext("2d");

        if (ctx) paint(ctx, withCamera, output.width, output.height);
      }

      const preview = previewRef.current;

      if (preview) {
        const ctx = preview.getContext("2d");

        if (ctx) paint(ctx, withCamera, preview.width, preview.height);
      }
    };

    frame = requestAnimationFrame(draw);

    return () => cancelAnimationFrame(frame);
  }, [active]);

  /* ---------------------------------------------------------------------- */
  /* Level meter                                                             */
  /* ---------------------------------------------------------------------- */

  useEffect(() => {
    if (status !== "live") return;

    // Ten times a second, not per frame: this one *does* set React state, and
    // a meter is legible at 10Hz. Thirty renders a second to move a bar four
    // pixels is the trade the render loop above exists to avoid.
    const timer = setInterval(() => setLevel(mixRef.current?.peak() ?? 0), 100);

    return () => clearInterval(timer);
  }, [status]);

  /* ---------------------------------------------------------------------- */
  /* Devices                                                                 */
  /* ---------------------------------------------------------------------- */

  const refreshDevices = useCallback(async () => {
    const all = await navigator.mediaDevices.enumerateDevices();

    setDevices({
      cameras: all.filter((d) => d.kind === "videoinput"),
      microphones: all.filter((d) => d.kind === "audioinput"),
    });
  }, []);

  useEffect(() => {
    void refreshDevices();

    navigator.mediaDevices.addEventListener("devicechange", refreshDevices);

    return () =>
      navigator.mediaDevices.removeEventListener(
        "devicechange",
        refreshDevices,
      );
  }, [refreshDevices]);

  const startCamera = useCallback(
    async (deviceId?: string) => {
      const stream = await withDevice(deviceId, (id) =>
        navigator.mediaDevices.getUserMedia({
          video: {
            deviceId: id ? { exact: id } : undefined,
            width: { ideal: CANVAS_WIDTH },
            height: { ideal: CANVAS_HEIGHT },
          },
          audio: false,
        }),
      );

      cameraRef.current?.getTracks().forEach((t) => t.stop());
      cameraRef.current = stream;

      const video = videoRef.current ?? document.createElement("video");
      video.srcObject = stream;
      video.muted = true;
      video.playsInline = true;
      await video.play();
      videoRef.current = video;

      setCameraOn(true);
      setCameraGeneration((n) => n + 1);

      // Labels are blank until permission has been granted once, so the list is
      // worth re-reading the moment it has been.
      await refreshDevices();
    },
    [refreshDevices],
  );

  const stopCamera = useCallback(() => {
    cameraRef.current?.getTracks().forEach((t) => t.stop());
    cameraRef.current = null;

    if (videoRef.current) videoRef.current.srcObject = null;
    videoRef.current = null;

    setCameraOn(false);
  }, []);

  const startMic = useCallback(
    async (deviceId?: string) => {
      const stream = await withDevice(deviceId, (id) =>
        navigator.mediaDevices.getUserMedia({
          audio: {
            deviceId: id ? { exact: id } : undefined,
            // On, all three. A host in a room with music playing out loud is
            // the normal case, not the exception, and raw microphone input in
            // that room is unlistenable.
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
          video: false,
        }),
      );

      micRef.current?.getTracks().forEach((t) => t.stop());
      micRef.current = stream;

      setMicOn(true);
      await refreshDevices();
    },
    [refreshDevices],
  );

  const stopMic = useCallback(() => {
    micRef.current?.getTracks().forEach((t) => t.stop());
    micRef.current = null;
    setMicOn(false);
  }, []);

  /* ---------------------------------------------------------------------- */
  /* What the video track actually is                                        */
  /* ---------------------------------------------------------------------- */

  /**
   * Whether the camera can be published directly, skipping the canvas.
   *
   * ## The canvas is a liability when it is drawing nothing of its own
   *
   * The compositor runs on `requestAnimationFrame`, which browsers throttle in
   * a hidden tab. That is survivable when the host is looking at the console,
   * and it is not survivable for the case this exists to serve: somebody
   * running OBS and sending its Virtual Camera here. They are, by definition,
   * working in another window - so the canvas stops being painted, and
   * `captureStream` happily keeps publishing the last frame it got. A frozen
   * picture with perfect audio, and no reason for the host to look.
   *
   * When the camera fills the frame and nothing is drawn over it, the canvas is
   * copying pixels for no reason anyway. Publishing the device's own track
   * removes the render loop from the path entirely - WebRTC pulls frames from
   * the camera whatever the tab is doing - and skips a scale-and-re-encode, so
   * the picture is sharper as well as more reliable.
   *
   * The preview stays honest through this: the compositor draws exactly the
   * camera, full-bleed, which is what is going out.
   */
  const passthrough = cameraOn && isBareCamera(scene.layers);

  /** The track that should be on the wire right now. */
  const videoTrack = useCallback((): MediaStreamTrack | null => {
    if (passthrough) {
      const track = cameraRef.current?.getVideoTracks()[0];

      if (track) return track;
    }

    return canvasTrackRef.current;
  }, [passthrough]);

  /* ---------------------------------------------------------------------- */
  /* Publishing                                                              */
  /* ---------------------------------------------------------------------- */

  const start = useCallback(async (whipUrl: string) => {
    setStatus("starting");
    setError(null);

    try {
      // Browsers start an AudioContext suspended until a gesture. This runs
      // from a click, which is the gesture - but only if it is awaited before
      // anything reads from the graph.
      await audioContext().resume();

      const output = outputRef.current;

      if (!output) throw new Error("The studio canvas is not ready.");

      const mix = createMix(micRef.current);
      mixRef.current = mix;

      /*
       * Captured once and kept, even while passthrough is publishing the camera
       * instead. `captureStream` hands back a fresh stream on every call, and
       * the host can turn an overlay back on at any moment - so the canvas
       * track has to be sitting there ready rather than created on demand
       * mid-broadcast.
       */
      canvasTrackRef.current =
        output.captureStream(CANVAS_FPS).getVideoTracks()[0] ?? null;

      const initial = videoTrack();
      const stream = new MediaStream([
        ...(initial ? [initial] : []),
        ...mix.stream.getAudioTracks(),
      ]);

      outgoingRef.current = stream;
      whipRef.current = whipUrl;

      /*
       * Publish, and keep publishing.
       *
       * `disconnected` is a blip WebRTC recovers from on its own, so it is
       * reported and otherwise left alone - tearing the session down on it
       * would turn every hiccup into a dropped broadcast.
       *
       * `failed` is different: ICE has given up and nothing will bring that
       * peer connection back. It used to end the show and tell the host to
       * press start again, which is no use to somebody mid-sentence. Now it
       * publishes the same stream over a new connection, backing off, and only
       * gives up once it has genuinely run out of tries.
       *
       * ## There is no automated test for this, and that is not an oversight
       *
       * `e2e/studio-reconnect-check.mjs` used to cover it by restarting the
       * MediaMTX container mid-broadcast. The ingest server is Cloudflare's
       * now, so the rug cannot be pulled from that end, and four ways of
       * pulling it from this end were tried and all of them failed *by
       * passing* - the broadcast carried on and the check went green having
       * provoked nothing:
       *
       * - CDP `Network.emulateNetworkConditions` with `offline: true`.
       *   Chromium's WebRTC stack does not go through the network service that
       *   CDP throttles. Playwright's `route` is worse: fetch layer only.
       * - `DELETE` on the WHIP session resource Cloudflare returns in the
       *   201's `Location`. Answered `200`, and the browser kept sending for a
       *   full minute - even ICE consent freshness (RFC 7675) never fired.
       * - `restartIce()` with `iceTransportPolicy: 'relay'` and no TURN server,
       *   so the restart can gather nothing. The existing candidate pair
       *   survived it.
       * - `pc.close()`, which does not reach this handler at all: it sets the
       *   state to `closed` without firing `connectionstatechange`, and the
       *   only state this reconnects on is `failed`.
       *
       * The check was deleted rather than left reporting a failure against
       * working code, which is how a suite gets ignored. If you are about to
       * rebuild it, one of the four above is probably what you were going to
       * try.
       */
      let attempts = 0;

      const clearRetry = () => {
        if (retryRef.current !== null) {
          window.clearTimeout(retryRef.current);
          retryRef.current = null;
        }
      };

      const attach = async (): Promise<void> => {
        const publication = await publish(whipUrl, stream);

        publicationRef.current = publication;

        publication.onStateChange((state) => {
          // A dead publication's events must not steer a live one. Without
          // this, the connection the reconnect replaced reports `failed` a
          // moment later and schedules a second reconnect against a session
          // that is already working.
          if (publicationRef.current !== publication) return;

          setConnection(state);

          if (state === "connected") {
            attempts = 0;
            setStatus("live");
            setError(null);
          }

          if (state === "failed") retry();
        });
      };

      const retry = (): void => {
        if (retryRef.current !== null) return;

        if (attempts >= RECONNECT_ATTEMPTS) {
          setStatus("error");
          setError(
            "Lost the stream server and could not get it back. Start again when your connection is steady.",
          );

          return;
        }

        const delay = Math.min(8000, 1000 * 2 ** attempts);

        attempts += 1;
        setStatus("reconnecting");
        setError(null);

        retryRef.current = window.setTimeout(() => {
          retryRef.current = null;

          // Sign the dead session off before opening another, or the ingest
          // server holds it until its own timeout and may refuse the new one
          // as a second publisher on the same path.
          void publicationRef.current?.stop();
          publicationRef.current = null;

          attach().catch(() => retry());
        }, delay);
      };

      retryClearRef.current = clearRetry;

      await attach();

      setStatus("live");
    } catch (cause) {
      mixRef.current?.stop();
      mixRef.current = null;

      setStatus("error");
      setError(
        cause instanceof SignalError || cause instanceof Error
          ? cause.message
          : "The studio could not start.",
      );
    }
  }, []);

  const stop = useCallback(async () => {
    // Before anything else: a reconnect already in flight would otherwise
    // re-open the broadcast the host just closed.
    retryClearRef.current?.();
    retryClearRef.current = null;

    await publicationRef.current?.stop();
    publicationRef.current = null;
    outgoingRef.current = null;
    whipRef.current = null;

    mixRef.current?.stop();
    mixRef.current = null;

    setStatus("idle");
    setConnection("new");
    setLevel(0);
  }, []);

  /*
   * Swap the video source when the reason for it changes.
   *
   * `replaceTrack` needs no renegotiation, so a host toggling an overlay
   * mid-broadcast costs a frame rather than a reconnection. The outgoing
   * stream is updated too, because a reconnect re-publishes it and would
   * otherwise offer whichever track was current when the broadcast began.
   */
  useEffect(() => {
    if (status !== "live" && status !== "reconnecting") return;

    const next = videoTrack();
    const stream = outgoingRef.current;

    if (!next || !stream) return;
    if (stream.getVideoTracks()[0] === next) return;

    for (const track of stream.getVideoTracks()) stream.removeTrack(track);

    stream.addTrack(next);
    void publicationRef.current?.replaceVideo(next);
  }, [status, videoTrack, cameraGeneration]);

  /* ---------------------------------------------------------------------- */
  /* Teardown                                                                */
  /* ---------------------------------------------------------------------- */

  useEffect(
    () => () => {
      /*
       * Devices are released on unmount, always.
       *
       * A camera light left on after the host navigated away is the single most
       * alarming thing a web app can do, and it is what happens if this is left
       * to garbage collection - `MediaStreamTrack` is not collected while the
       * device is open.
       */
      retryClearRef.current?.();
      publicationRef.current?.stop();
      mixRef.current?.stop();
      cameraRef.current?.getTracks().forEach((t) => t.stop());
      micRef.current?.getTracks().forEach((t) => t.stop());
    },
    [],
  );

  return useMemo(
    () => ({
      outputRef,
      previewRef,
      status,
      error,
      connection,
      devices,
      cameraOn,
      micOn,
      level,
      passthrough,
      startCamera,
      stopCamera,
      startMic,
      stopMic,
      start,
      stop,
      setDeckLevel: (value: number) => mixRef.current?.setDeckLevel(value),
      setMicLevel: (value: number) => mixRef.current?.setMicLevel(value),
    }),
    [
      status,
      error,
      connection,
      devices,
      cameraOn,
      micOn,
      level,
      passthrough,
      startCamera,
      stopCamera,
      startMic,
      stopMic,
      start,
      stop,
    ],
  );
}
